import React from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/Preview";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import { useGeneratedDocumentsTab } from "./useGeneratedDocumentsTab";

export const GeneratedDocumentsTab: React.FC = () => {
  const {
    selectedTemplateId,
    setSelectedTemplateId,
    selectedClientId,
    setSelectedClientId,
    selectedBankId,
    setSelectedBankId,
    effectiveDate,
    setEffectiveDate,
    customVariablePlaceholders,
    customVariableValues,
    setCustomVariableValue,
    clients,
    clientsLoading,
    relatedData,
    relatedDataLoading,
    pendingDocuments,
    generatingAll,
    templatesWithPlaceholders,
    requiresBankAccount,
    canAddToPending,
    hasPendingDocs,
    handleAddToPending,
    handleRemovePending,
    handleDownload,
    handleGenerateOne,
    handleSaveDocument,
    handleGenerateAll,
    handlePreview,
  } = useGeneratedDocumentsTab();

  return (
    <Box sx={{ p: 3 }}>
      {/* Document Generation Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
          Document Generation Settings - Step 1
        </Typography>

        {/* Template Selection */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Select Template
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Choose a template...
              </MenuItem>
              {templatesWithPlaceholders.map(t => (
                <MenuItem key={t.identifier} value={t.identifier}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Preview Template">
            <span>
              <IconButton
                onClick={handlePreview}
                disabled={!selectedTemplateId}
                color="primary"
              >
                <PreviewIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {templatesWithPlaceholders.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No templates with placeholders found. Go to Templates tab to create one.
          </Alert>
        )}

        {/* Client Selection */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Select Client
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 3 }} disabled={!selectedTemplateId}>
          <Select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {clientsLoading ? "Loading clients..." : "Choose a client..."}
            </MenuItem>
            {clients.map(c => (
              <MenuItem key={c.identifier} value={c.identifier}>
                {c.first_name} {c.last_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Bank Account Selection (if needed) */}
        {requiresBankAccount && relatedData && relatedData.bank_accounts.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Select Bank Account
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                value={selectedBankId}
                onChange={(e) => setSelectedBankId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  Use first available
                </MenuItem>
                {relatedData.bank_accounts.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.Bank} {b.Bank_Account_Number ? `****${b.Bank_Account_Number.slice(-4)}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {relatedDataLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading client data...
            </Typography>
          </Box>
        )}

        {/* Custom Fields */}
        {customVariablePlaceholders.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Custom Fields
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {customVariablePlaceholders.map(p => (
                <TextField
                  key={p.key}
                  label={p.label}
                  size="small"
                  fullWidth
                  value={customVariableValues[p.key] || ''}
                  onChange={(e) => setCustomVariableValue(p.key, e.target.value)}
                />
              ))}
            </Box>
          </>
        )}

        {/* Effective Date */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Effective Date
        </Typography>
        <TextField
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 3 }}
        />

        {/* Add to Pending Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddToPending}
            disabled={!canAddToPending}
          >
            Add to Pending
          </Button>
        </Box>
      </Paper>

      {/* Pending Documents */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ color: "primary.main" }}>
            Pending Documents
          </Typography>
          {hasPendingDocs && (
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleGenerateAll}
              disabled={generatingAll}
            >
              {generatingAll ? "Generating..." : "Generate All"}
            </Button>
          )}
        </Box>

        {pendingDocuments.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <AddIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Pending Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure settings above and click "Add to Pending"
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Bank</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingDocuments.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.templateName}</TableCell>
                    <TableCell>{doc.clientName}</TableCell>
                    <TableCell>{doc.bankName || "-"}</TableCell>
                    <TableCell>{doc.effectiveDate}</TableCell>
                    <TableCell>
                      {doc.status === "error" ? (
                        <Tooltip title={doc.errorMessage || "Generation failed"} arrow>
                          <Chip
                            label={doc.errorMessage || "error"}
                            size="small"
                            color="error"
                            sx={{ maxWidth: 200 }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={doc.status}
                          size="small"
                          color={
                            doc.status === "done" ? "success" :
                            doc.status === "generating" ? "warning" :
                            "default"
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {doc.status === "pending" && (
                        <>
                          <Tooltip title="Generate & Download">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleGenerateOne(doc)}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Save Document (for Flows)">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSaveDocument(doc)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemovePending(doc.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {doc.status === "generating" && (
                        <CircularProgress size={20} />
                      )}
                      {doc.status === "done" && doc.generatedDocId && (
                        <>
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(doc.generatedDocId!, `${doc.templateName} - ${doc.clientName} - ${doc.effectiveDate}`)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove from list">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => handleRemovePending(doc.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {doc.status === "error" && (
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemovePending(doc.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};
